import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn.js';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, hint, className, containerClassName, id, rows = 4, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label && (
        <label htmlFor={fieldId} className="text-xs font-semibold text-ink-2">
          {label}
        </label>
      )}

      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'w-full rounded-control border bg-white px-3 py-2 text-sm text-ink',
          'placeholder:text-ink-soft',
          'transition-colors focus:outline-none focus:ring-2',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken',
          error
            ? 'border-danger focus:border-danger focus:ring-danger/30'
            : 'border-line focus:border-go focus:ring-go/30',
          className,
        )}
        {...rest}
      />

      {error ? (
        <p id={`${fieldId}-error`} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="text-xs text-ink-soft">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
